import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import type { Database } from "@/lib/supabase-types";

export type TenantIssueRow = Database["public"]["Tables"]["issues"]["Row"];
export type TenantIssueFilter = "all" | "open" | "closed";

const OPEN_FILTER_STATUSES = ["open", "in_progress", "waiting"] as const;
const CLOSED_FILTER_STATUSES = ["resolved", "closed"] as const;

/** Read-only history for existing requests. New tenant contact is handled by
 * Email or WhatsApp; this hook never creates or replies to a portal ticket. */
export function useTenantIssueHistory(filter: TenantIssueFilter) {
  const { user } = useAuth();
  const userId = user?.id ?? "";
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => ["tenant", "issues", userId, filter] as const,
    [filter, userId]
  );
  const query = useQuery({
    queryKey,
    enabled: !!userId,
    queryFn: async () => {
      let request = supabase
        .from("issues")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      if (filter === "open")
        request = request.in(
          "status",
          OPEN_FILTER_STATUSES as unknown as string[]
        );
      if (filter === "closed")
        request = request.in(
          "status",
          CLOSED_FILTER_STATUSES as unknown as string[]
        );
      const { data, error } = await request;
      if (error) throw error;
      return (data ?? []) as TenantIssueRow[];
    },
  });

  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`tenant-issue-history:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "issues",
          filter: `user_id=eq.${userId}`,
        },
        () => queryClient.invalidateQueries({ queryKey: ["tenant", "issues"] })
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [queryClient, userId]);

  return query;
}
